import { FormatType } from '../../types';
import { getFormatter } from '../../utilities';
import { PlotTooltip, type TooltipPosition } from '../plot-tooltip';
import styles from './chord-chart.module.css';
import type { ChordFlowMode, ChordShareDirection } from './types';

type ChordRibbonTooltipProps = {
  sourceLabel: string;
  targetLabel: string;
  flow: number;
  share: number | undefined;
  shareDirection: ChordShareDirection;
  isOpen: boolean;
  cursor: TooltipPosition | null;
  flowMode: ChordFlowMode;
};

const format = getFormatter(FormatType.INTEGER);
const percentFormat = getFormatter(FormatType.PERCENTAGE);

export const ChordRibbonTooltip = ({
  sourceLabel,
  targetLabel,
  flow,
  share,
  shareDirection,
  isOpen,
  cursor,
  flowMode,
}: ChordRibbonTooltipProps) => {
  if (!isOpen || cursor === null) return null;
  return (
    <PlotTooltip
      isVisible
      x={cursor.x}
      y={cursor.y}
      placement="vertical"
      space="viewport"
    >
      <TooltipContent
        sourceLabel={sourceLabel}
        targetLabel={targetLabel}
        flow={flow}
        share={share}
        shareDirection={shareDirection}
        flowMode={flowMode}
      />
    </PlotTooltip>
  );
};

const TooltipContent = ({
  sourceLabel,
  targetLabel,
  flow,
  share,
  shareDirection,
  flowMode,
}: {
  sourceLabel: string;
  targetLabel: string;
  flow: number;
  share: number | undefined;
  shareDirection: ChordShareDirection;
  flowMode: ChordFlowMode;
}) => {
  const isNet = flowMode === 'net';
  const flowLabel = isNet ? 'Net Transitions' : 'Transitions';
  // Shares are computed against the gross row/column total and don't map to
  // the residual net value, so we drop them entirely in net flow mode.
  const showShare = !isNet && share !== undefined && share > 0;
  const shareLabel =
    shareDirection === 'inflows' ? 'Inflow share' : 'Outflow share';
  return (
    <div className={styles.tooltipBlock}>
      <div className={styles.tooltipTitle}>
        {sourceLabel}
        <span className={styles.tooltipArrow} aria-hidden>
          →
        </span>
        {targetLabel}
      </div>
      <dl className={styles.tooltipMetrics}>
        <dt>{flowLabel}</dt>
        <dd>{format(flow)}</dd>
        {showShare && (
          <>
            <dt>{shareLabel}</dt>
            <dd>{percentFormat(share)}</dd>
          </>
        )}
      </dl>
    </div>
  );
};
